import { Button } from '@design-system';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { pathFor } from '../../../helpers/links';
import { useGetDrawByProductIDsQuery } from '../../../types/generated';
import { TrackLinkClicked } from '../../../utils/analytics';
import ProductTile from '../../ProductTile';

type Props = {
  recommendations: Product[];
  mediumTag?: PrintMedium;
  analytics?: Analytics;
};

const Recommendations: React.FC<Props> = ({
  analytics,
  mediumTag,
  recommendations,
}) => {
  const { t } = useTranslation('common');

  const productIDs = recommendations.map((p) =>
    shopifyGraphqlIdToAdminId(p.id),
  );
  const { data: draws } = useGetDrawByProductIDsQuery({
    variables: { productIDs },
    skip: productIDs.length === 0,
  });

  if (!recommendations?.length) return null;

  const productTileComponents = recommendations
    .slice(0, 4)
    .map((product, index) => {
      const draw = draws?.getDrawByProductIDs?.find((d) => {
        return d.productId === shopifyGraphqlIdToAdminId(product.id);
      });

      return (
        <ProductTile
          key={product.id}
          analytics={
            analytics && {
              page: analytics.page,
              context: analytics.context,
              position: index,
            }
          }
          draw={draw}
          product={product}
        />
      );
    });

  const areAllSameMedium = recommendations.every(
    (product) => product.medium === mediumTag,
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full items-center gap-3">
        <h2 className="w-full flex-1 text-md text-neutral-4 md:text-lg">
          {mediumTag && areAllSameMedium
            ? t(MEDIUM_TRANSLATIONS[mediumTag])
            : t('recommendations.titles.default')}
        </h2>

        <div className="shrink-0">
          <Button
            as="a"
            href={pathFor({ page: 'WORKS_INDEX' })}
            label={t('viewAll')}
            onClick={() => {
              if (analytics) {
                TrackLinkClicked({
                  page: analytics.page,
                  context: analytics.context,
                  title: 'See all',
                  url: pathFor({ page: 'WORKS_INDEX' }),
                  isExternal: false,
                });
              }
            }}
            size="sm"
            variant="tertiary"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
        {productTileComponents}
      </div>
    </div>
  );
};

export default Recommendations;

const MEDIUM_TRANSLATIONS: Record<PrintMedium, string> = {
  'Collectible': 'recommendations.titles.collectibles',
  'NFTs': 'recommendations.titles.nfts',
  'Other': 'recommendations.titles.default',
  'Print edition': 'recommendations.titles.prints',
  'Sculpture edition': 'recommendations.titles.sculptures',
  'Studio works': 'recommendations.titles.studioWorks',
};
